import { type ComponentProps, type MouseEvent, useState } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { type CardIdType } from '@/defs/card'
import { type InViewStatus } from '@/hooks/useInView'
import { usePointerType } from '@/hooks/usePointerType'
import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { getCardName } from '@/utils/cardUtil'
import { cn } from '@/utils/commonUtil'

import { CardFace } from './CardFace/CardFace'
import { CardFacePrices } from './CardFace/CardFacePrices/CardFacePrices'
import styles from './CardFaces.module.scss'

type CardFaceProps = ComponentProps<typeof CardFace>

type FaceType = 'back' | 'front'

type Props = {
  animate?: boolean
  back?: CardFaceProps
  cardId?: CardIdType
  front: CardFaceProps
  inViewStatus?: InViewStatus
  linkProps?: {
    href: string
    newTab?: boolean
  }
  onClick?: (e: MouseEvent<HTMLElement>) => void
  showPrices?: boolean
  variant?: 'standard' | 'dfc' | 'partner'
}

export const CardFaces = ({
  animate,
  back,
  cardId,
  front,
  inViewStatus = 'inView',
  linkProps,
  onClick,
  showPrices = true,
  variant,
}: Props) => {
  const [raised, setRaised] = useState<FaceType>('front')
  const { isTouch, onPointerDownCapture } = usePointerType()
  const dispatch = useReduxDispatch()

  const hasPrices = !!cardId && showPrices
  const name = variant === 'dfc' ? getCardName(front.name, back?.name) : front.name

  const onClickFace = (e: MouseEvent, face: FaceType) => {
    if (face === raised || !isTouch()) return
    e.preventDefault()
    setRaised(face)
  }

  const onClickName = () => {
    if (!name) return
    void (async () => {
      try {
        await navigator.clipboard.writeText(name)
        dispatch(
          toastActions.add(
            <>
              Copied <strong>{name}</strong>
            </>,
          ),
        )
      } catch {
        dispatch(toastActions.add(<>Unable to copy</>))
      }
    })()
  }

  return (
    <div
      className={cn(styles.container, { animate, hasPrices, inViewStatus, styles, variant })}
      onPointerDownCapture={onPointerDownCapture}
    >
      <div className={styles.faces}>
        {back && (
          <div
            className={cn(styles.cardBack, { raised: raised === 'back', styles })}
            onClickCapture={(e) => onClickFace(e, 'back')}
          >
            <CardFace {...back} inViewStatus={inViewStatus} linkProps={linkProps} onClick={onClick} />
          </div>
        )}
        <div
          className={cn(styles.cardFront, { raised: raised === 'front', styles })}
          onClickCapture={(e) => onClickFace(e, 'front')}
        >
          <CardFace {...front} inViewStatus={inViewStatus} linkProps={linkProps} onClick={onClick} />
          {name && (
            <ButtonRoot className={styles.name} onClick={onClickName}>
              {name}
            </ButtonRoot>
          )}
        </div>
      </div>
      {hasPrices && (
        <div className={styles.prices}>
          <CardFacePrices cardId={cardId} enabled={inViewStatus === 'inView'} />
        </div>
      )}
    </div>
  )
}
